import qs from 'querystring';
import { compact } from 'lodash';
import { SearchResultResponse } from 'lib/types/globalSearch';
import { DISPUTED_CREDIT_STATUS } from 'lib/constants';
import {
  AdminMilestoneResponse,
  AdminRolesResponse,
  AdminTagValuesResponse,
  CreatorTagResponse,
  FailedPostResponse,
  MatchResponse,
  ReferralsResponse,
  SimilarImageResponse,
  NetworkAccountsSearchResponse,
  PostSearchResponse,
  ScraperStatusResponse,
  DeactivationCheckResponse,
  JobSearchResponse,
  PostServiceStats,
  MyGondolaResponse,
  MyGondolaMilestones,
  NotificationStatus,
  ConceptSearchResults,
  ProjectSearchResults,
  PostingAccountsSearchResponse,
  AdminTeamsResponse,
  AdminTubularResponse,
  TeamCreatorsResponse,
  ProjectApplicantsResponse,
} from 'server/api/types';
import { QueryResult, QueryExplanation } from 'server/api/services/naturalLanguageQuery';
import { TeamCreator, TeamTableConfig, TeamComment } from 'lib/types/teams';
import { NaturalLanguageSearchResponse } from 'server/api/services/creatorSearch/ml/types';
import { TeamAccountSettings } from 'server/api/services/teams/index';
import { PostSearchCriteria } from 'lib/types/filters';
import { GoogleAuthPayload } from 'server/api/services/auth/google';
import { CreatorSearchCriteria, CreatorSearchESResponse } from 'server/api/services/creatorSearch/types';
import {
  AnalyticsCriteria,
  GroupedTimeSeriesResponse,
  StreamlinedAnalyticsResponse,
  UserWithFollowerDelta,
} from 'server/api/services/analytics/types';
import { TokenExchangeResponse } from 'contentAPIs/google/types';
import { ManualPost } from 'server/api/schemas/posts';
import {
  ApplicationQuestion, Job, JobApplication, NewApplication, JobStats,
} from 'lib/types/jobs';
import { JobSchemaType } from 'server/api/schemas/jobs';
import Stripe from 'stripe';
import { Report, ReportType } from 'lib/types/reports';
import {
  NetworkStatus, Link, Track, TubularCache,
  AppleWebSignInCredential, HeardAboutOption,
  MediaSourceType, SavedReport, ListOrProjectWithStages, ListUser,
} from 'lib/types';
import {
  BTSItem, ProjectCollaborator, Deliverable, CostCalculationResult, ProjectCostSummary,
  ProjectPostCost, ProjectAnalyticsResponse, ProjectSummary, Candidate,
} from 'lib/types/projects';
import { ProjectRole, BudgetEstimate } from 'lib/types/projectRoles';
import MatchPost from 'server/models/MatchPosts';
import { CacheSearchCriteria } from 'lib/types/caches';
import { GlobalAnalyticsResponse } from 'server/es/indexes/posts/search/types';
import { SourcingSearchCriteria } from 'server/api/services/teams/types';
import {
  DashboardChatContextPayload,
  DashboardChatContextResponse,
  DashboardChatRequest,
  DashboardChatEnqueueResponse,
  DashboardChatStatusResponse,
} from 'lib/types/dashboardChat';
import {
  Audit,
  History,
  BlogPost,
  FeedbackType,
  KeyRole,
  Notification,
  Pin,
  Post,
  Role,
  RoleTag,
  Milestone,
  EmailCredential,
  List,
  AddList,
  Stats,
  ListVote,
  Subscription,
  EventSignup,
  SensitiveContentTypes,
  OpenAIImagesResponse,
  Media,
  ContentList,
  PostScore,
} from './types/index';
import {
  AdminViewUserResponse,
  CreatorsListUser,
  NetworkUser,
  User,
  UserSettings,
  UserData,
  NextStep,
  CredentialsForEmail,
  AllNetworks,
} from './types/users';
import {
  MatchAdminQuery, SimilarImagesQuery, PostingAccountCriteria, ScraperAdminCriteria,
} from './types/query';
import {
  EditCredit, Credit, CreditType, DisputedCredit, CreateCreditsPayload,
} from './types/credits';
import { TagType, TagValue } from './types/tags';
import { TrackedEvent } from './hooks/useEventTracking';
import {
  AdminTeamQueryParams,
  Team, TeamAccount, TeamMember, TeamSubscriptionInvoice, ExternalApiPermission,
  TeamBillingInfo,
  CreatorStage,
} from './types/teams';
import { StripeCustomerResponse, StripeSessionResponse } from './types/stripe';
import { StripePaymentMethod } from './types/paymentMethods';
import { ACTIONS } from './types/events';
import { WorkedTogetherResponse, LimitedUseResponse } from './types/workedTogether';
import { Concept, Project } from './types/projects';
import { CompareResultsResponse, MatchMetrics, MediaComparisonGroup } from './types/mediaComparisons';
import { ApiCacheResponse } from './types/caches';
import { ScraperAccount } from './types/scraper';
import {
  AutoCredit, AutoCreditCount, Connection, PostingAccount,
} from './types/postingAccounts';
import {
  PostMetrics, PostMetricsUpdate, PostToImport, PostWithDelta, ProjectPost,
} from './types/posts';
import { NetworkAccount } from './types/networkAccounts';
import { TimeoutError } from './constants/errors';

const REQUEST_METHODS: { [method: string]: string } = {
  GET: 'GET',
  DELETE: 'DELETE',
  POST: 'POST',
  PUT: 'PUT',
};

interface Query {
  [key: string]: string | number | (string | number) | undefined;
}
/**
 * 30 seconds is the max because the load balancer will timeout after 30 seconds
 * We do slightly less as we prefer to timeout before we get a 502.
*/
const THIRTY_SECONDS = 28000;

/**
 * We're going to automatically throw an error when a fetch takes longer than 30 seconds.
 * Many slow requests now have 20 second timeouts on the backend so we'll hopefully get a timeout
 * error back before then.
 * */
function fetchWithTimeout(
  url: URL | string, options?: any, timeout = THIRTY_SECONDS,
): Promise<Response> {
  const errorMessage = 'Timeout accessing Gondola. Try again later.';
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) => setTimeout(() => reject(new TimeoutError(errorMessage)), timeout)),
  ]) as Promise<Response>;
}

/**
 * ABORT_REQUEST_CONTROLLERS stores AbortController by key. Currently,
 * we construct a string ${method}-${path} e.g. 'get-/posts/explore'
 *
 * via https://pgarciacamou.medium.com/using-abortcontroller-with-fetch-api-and-reactjs-8d4177e51270
 */
const ABORT_REQUEST_CONTROLLERS = new Map<string, AbortController>();

/**
 * abortRequest looks up an AbortController via its string key
 * and sends an abort signal.
 */
export function abortRequest(key: string, reason = 'CANCELLED') {
  ABORT_REQUEST_CONTROLLERS.get(key)?.abort?.(reason);
}

/**
 * abortAndGetSignal aborts a request (via `abortRequest`) and then
 * sets a new AbortController for the same key in ABORT_REQUEST_CONTROLLERS.
 */
function abortAndGetSignal(key: string | undefined) {
  if (!key) {
    return undefined;
  }
  abortRequest(key); // abort previous request, if any
  const newController = new AbortController();
  ABORT_REQUEST_CONTROLLERS.set(key, newController);
  return newController.signal;
}

export class ApiError extends Error {
  errorType: number | undefined;

  id?: string;

  constructor(errResponse: any) {
    super(errResponse?.message || 'Unknown API error');
    this.errorType = errResponse?.errorType || -1;
    this.id = errResponse?.id;
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export const buildUrl = (path: string, query = {}) => compact([`/api${path}`, qs.stringify(query)]).join('?');

export const apiClient = (currentUser?: User | null) => {
  const buildHeaders = (ctx: any, isJsonBody: boolean) => {
    const token = currentUser?.token || null;
    const headers = new Headers();
    headers.append('Accept', 'application/json');
    if (token) {
      headers.append('Authorization', token);
    }
    const hasCookie = ctx && ctx?.req && ctx?.req?.headers?.cookie;
    if (hasCookie) {
      headers.append('cookie', ctx.req.headers.cookie);
    }
    if (isJsonBody) {
      headers.append('Content-Type', 'application/json');
    }

    if (typeof localStorage !== 'undefined') {
      const featureFlag = localStorage.getItem('featureFlag');
      /** Send the feature flag (if any) as a request header */
      if (featureFlag) {
        headers.append('Feature-Flag', featureFlag);
      }
    }
    return headers;
  };

  const buildRequestOptions = (
    method: string,
    query: any,
    body: any,
    ctx: any,
    isJsonBody: boolean,
    signalKey: string | undefined,
  ) => {
    const headers = buildHeaders(ctx, isJsonBody);
    const methodsNoBody = [REQUEST_METHODS.GET, REQUEST_METHODS.DELETE];

    const signal = abortAndGetSignal(signalKey);
    if (methodsNoBody.includes(method)) {
      return {
        headers,
        method,
        signal,
      };
    }
    return {
      headers,
      method,
      body,
      signal,
    };
  };

  async function makeRequest<T>(
    method: string, path: string, query = {}, body: any, ctx: any, isJsonBody = false,
  ): Promise<T> {
    const url = buildUrl(path, query);
    const signalKey = ctx?.useAbort ? `${method}-${path}` : undefined;
    const timeout = ctx?.customTimeout ? ctx?.customTimeout : undefined;
    const requestOptions = buildRequestOptions(method, query, body, ctx, isJsonBody, signalKey);

    let response: Response | undefined;
    let json;
    try {
      response = await fetchWithTimeout(
        url.toString(),
        requestOptions,
        timeout,
      );
      json = await response.json();
    } catch (e: any) {
      if (e === 'CANCELLED' || e.name === 'AbortError' || e.name === 'TimeoutError') {
        // Let AbortError and TimeoutError pass through so we can handle them in the caller.
        throw (e);
      }
      // if there is a problem parsing the json and there was already an issue with the response
      // throw an API error
      if (!response?.ok) {
        throw new ApiError({ message: response?.statusText });
      }
    }
    if (json?.success) {
      return (ctx && ctx.returnFullJson) ? json : json.data;
    }
    throw new ApiError(json);
  }

  function hasToken() {
    return !!currentUser?.token;
  }

  async function get<T>(path: string, query?: any, ctx?: any): Promise<T> {
    return makeRequest<T>(
      REQUEST_METHODS.GET, path, query, {}, ctx,
    );
  }

  async function put<T>(path: string, body?: any, ctx?: any): Promise<T> {
    return makeRequest<T>(
      REQUEST_METHODS.PUT, path, {}, JSON.stringify(body || {}), ctx, true,
    );
  }

  async function post<T>(path: string, body?: any, ctx?: any): Promise<T> {
    return makeRequest<T>(
      REQUEST_METHODS.POST, path, {}, JSON.stringify(body || {}), ctx, true,
    );
  }

  async function httpDelete<T>(path: string, query?: any, ctx?: any): Promise<T> {
    return makeRequest<T>(
      REQUEST_METHODS.DELETE, path, query, {}, ctx,
    );
  }

  async function putFile<T>(path: string, data: any): Promise<T> {
    return makeRequest<T>(
      REQUEST_METHODS.PUT, path, {}, data, null,
    );
  }

  async function postFile<T>(path: string, data: any, ctx?: any): Promise<T> {
    return makeRequest<T>(
      REQUEST_METHODS.POST, path, {}, data, ctx,
    );
  }

  async function getCursorPagination<T>(
    path: string, query: any, ctx = {}, before?: string, after?: string,
  ): Promise<T> {
    if (before) {
      const queryWithBeforeCursor = { ...query, ...{ before } };
      return get(path, queryWithBeforeCursor, ctx);
    }
    if (after) {
      const queryWithAfterCursor = { ...query, ...{ after } };
      return get(path, queryWithAfterCursor, ctx);
    }
    return get(path, query, ctx);
  }

  // ----- api/auth
  const getAuth = async (ctx?: any): Promise<User | null> => {
    try {
      return get('/auth', null, ctx);
    } catch (e) {
      // If this fails for any reason, return null
      return null;
    }
  };

  // ----- api/roles
  const listAllRoles = async (): Promise<Role[]> => get('/roles');
  const listAllRolesAdmin = async (query: any): Promise<AdminRolesResponse> => get('/roles/admin', query);
  const getUserRoles = async (
    userId: number,
    onlyUserRoles: boolean = false,
  ): Promise<Role[]> => get(
    `/roles/user/${userId}`, { onlyUserRoles },
  );
  const createSuggestedRole = async (suggestedRole: string, exampleTwitterHandle: string) => post('/roles/suggest', { suggestedRole, exampleTwitterHandle });
  const createRole = async (role: Role): Promise<Role> => post('/roles', role, { returnFullJson: true });
  const updateRole = async (role: Role): Promise<Role> => put(`/roles/${role.id}`, role, { returnFullJson: true });
  const getRoleById = async (roleId: number): Promise<Role> => get(`/roles/${roleId}`);
  const getUserHiddenKeyRoles = async (userId: number): Promise<KeyRole[]> => get(`/roles/${userId}/userHiddenKeyRoles`);
  const createHiddenKeyRole = async (roleId: number): Promise<Role> => post(`/roles/${roleId}/addHiddenKeyRole`);
  const deleteHiddenKeyRole = async (roleId: number) => post(`/roles/${roleId}/removeHiddenKeyRole`);
  const listAllRoleTags = async (): Promise<RoleTag[]> => get('/roles/tags');
  const getRoleTagSuggestions = async (roleTagId: number): Promise<RoleTag[]> => get(`/roles/${roleTagId}/suggest`);

  // ----- api/tags
  const listTagTypes = async (): Promise<TagType[]> => get('/tags/types');
  const listAllTagValuesAdmin = async (query: any): Promise<AdminTagValuesResponse> => get('/tags/admin', query);
  const tagValuesSearch = async (query: string, tagTypeId: number): Promise<TagValue[]> => get(
    '/tags/values/search/value', { query, tagTypeId },
  );
  const createNewTagValue = async (
    tagTypeId: number, value: string, weighting?: number | null,
  ): Promise<TagValue> => post(
    '/tags', { value, tagTypeId, weighting },
  );
  const updateTagValue = async (tagValueId: number, tagValue: Partial<TagValue>) => put(`/tags/values/${tagValueId}`, tagValue, { returnFullJson: true });
  const deleteTagValue = async (tagValueId: number) => httpDelete(`/tags/values/${tagValueId}`);
  const createBulkTags = async (payload: any) => post('/tags/bulk', payload);
  const listUserTags = async (userId: number, tagTypeId: number): Promise<TagValue[]> => get(`/tags/${userId}/`, { tagTypeId });
  const suggestTagsForPostId = async (
    tagTypeId: number, postId: number,
  ): Promise<TagValue[]> => get(
    `/tags/suggest/${postId}`, { tagTypeId },
  );
  const getCreatorTagsByUser = async (userId: number): Promise<CreatorTagResponse> => get(`/tags/${userId}/creatorTagsByUser`);
  const addCreatorTag = async (tagValue: string) => post('/tags/creatorTag', {
    value: tagValue,
  });
  const deleteCreatorTag = async (tagId: number) => httpDelete(`/tags/creatorTag/${tagId}`);

  // ----- api/admin/reports
  const listReports = async (): Promise<ReportType[]> => get('/admin/reports');
  const getReport = async (key: string): Promise<Report> => get('/admin/reports/run', { type: key }, { useAbort: true });

  // ----- api/posts
  const createPinnedPost = async (postId: number): Promise<Post> => post(`/posts/${postId}/pin`);
  const deletePinnedPost = async (postId: number) => post(`/posts/${postId}/unpin`);
  const getPostById = async (postId: number): Promise<Post> => get(`/posts/${postId}`);
  const getPostByUrl = async (url: string): Promise<Post> => get('/posts/url', { url });
  const createPostByUrl = async (url: string): Promise<Post> => post('/posts', { url });
  const searchAccountNames = async (query: string): Promise<string> => get('/posts/accounts', { q: query });
  const deletePost = async (postId: number) => httpDelete(`/posts/${postId}`);
  const unDeletePost = async (postId: number) => put(`/posts/${postId}/undelete`);
  const scrapePostMetrics = async (postId: number): Promise<PostWithDelta> => get(`/posts/${postId}/scrape`);
  const queueScrapePost = async (postId: number): Promise<void> => get(`/posts/${postId}/enqueueScrape`);
  const addTagToPost = async (postId: number, tagTypeId: number, tagValue: string, tagValueId?: number) => post(`/posts/${postId}/tags`, {
    tagTypeId,
    tagValue,
    tagValueId,
  });
  const deleteTagFromPost = async (postId: number, tagId: number) => httpDelete(`/posts/${postId}/tags/${tagId}`);
  const toggleIsLive = async (postId: number, isLive: boolean) => post(`/posts/${postId}/isLive`, { isLive });
  const toggleStatsPaused = async (postId: number, statsPaused: boolean) => post(`/posts/${postId}/statsPaused`, { statsPaused });
  const toggleIsFeatured = async (postId: number, isFeatured: boolean) => post(`/posts/${postId}/featured`, { isFeatured });
  const toggleSensitiveContent = async (postId: number, sensitiveContent: SensitiveContentTypes) => post(`/posts/${postId}/sensitiveContent`, { sensitiveContent });
  const createManualPost = async (postData: ManualPost): Promise<Post> => post('/posts/manual', postData);
  const uploadMedia = async (file: FormData, filename: string, folder: string, sourceId?: string, sourceType?: string, skipEmbedding?: boolean): Promise<Media[]> => postFile(`/uploads/media?filename=${filename}&folder=${folder}&sourceId=${sourceId}&sourceType=${sourceType}&skipEmbedding=${skipEmbedding}`, file, { customTimeout: THIRTY_SECONDS });
  const uploadCompressedMedia = async (file: FormData, filename: string, folder: string): Promise<Media[]> => postFile(`/uploads/compressedMedia?&folder=${folder}&filename=${filename}`, file);
  const uploadLinkMedia = async (url: string, source: MediaSourceType, folder?: string): Promise<Media> => post('/uploads/link', { url, source, folder });
  const findMatchesForUpload = async (pHash: string, distanceThreshold: number): Promise<Post[]> => get('/posts/findSimilar', { pHash, distanceThreshold });
  const findMatchesForUploadViaUrl = async (url: string, minScore?: number): Promise<CompareResultsResponse> => get('/posts/compare/findSimilarUrl', { url, minScore });
  const findMatchesForUploadViaImage = async (mediaId: number, minScore?: number): Promise<CompareResultsResponse> => get('/posts/compare/findSimilarImg', { mediaId, minScore });
  const getMediaComparisonResults = async (publicId: string): Promise<CompareResultsResponse> => get(`/posts/compare/${publicId}`);
  const runMediaComparison = async (url: string | undefined, mediaId?: number, minScore?: number): Promise<CompareResultsResponse> => post('/posts/compare', { mediaId, url, minScore });
  const updateMediaComparison = async (comparisonId: number, creditedUserId: number, creditedRoleId: number, creditAlreadyMatchedPosts: boolean): Promise<CompareResultsResponse> => put(`/posts/compare/${comparisonId}`, { creditedUserId, creditedRoleId, creditAlreadyMatchedPosts });
  const updateMediaComparisonGroup = async (groupId: number, comparison: any): Promise<CompareResultsResponse> => put(`/posts/compare/group/${groupId}`, comparison);
  const updateMediaComparisonGroupCredits = async (groupId: number, creditedUserId: number, creditedRoleId: number, creditAlreadyMatchedPosts: boolean): Promise<CompareResultsResponse> => put(`/posts/compare/group/${groupId}/credits`, { creditedUserId, creditedRoleId, creditAlreadyMatchedPosts });
  const createMediaComparisonGroup = async (group: Partial<MediaComparisonGroup>): Promise<MediaComparisonGroup> => post('/posts/compare/group', group);
  const getMediaComparisonGroup = async (publicId: string): Promise<MediaComparisonGroup> => get(`/posts/compare/groups/${publicId}`);
  const getMediaComparisonGroups = async (): Promise<MediaComparisonGroup[]> => get('/posts/compare/groups');
  const deleteMediaComparisonGroup = async (groupId: number) => httpDelete(`/posts/compare/group/${groupId}`);
  const addMediaToComparisonGroup = async (groupId: number, mediaIds: number[]): Promise<MediaComparisonGroup> => post(`/posts/compare/group/${groupId}/media`, { mediaIds });
  const deleteComparisonFromComparisonGroup = async (groupId: number, comparisonId: number): Promise<MediaComparisonGroup> => httpDelete(`/posts/compare/group/${groupId}/comparison/${comparisonId}`);
  const findPostsViaText = async (text: string): Promise<Post[]> => get('/posts/compare/findImagesFromText', { text });
  const reindexPost = async (postId: number): Promise<void> => post(`/posts/${postId}/reindex`);
  const getTrendingPosts = async (): Promise<Post[]> => get('/posts/trending');
  const getConceptsForPost = async (postId: number): Promise<Concept[]> => get(`/posts/${postId}/concepts`);
  const getCompareUses = async (): Promise<LimitedUseResponse> => get('/posts/compare/uses');
  const ignoreSuggestedCredit = async (postId: number): Promise<void> => httpDelete(`/posts/${postId}/suggestedCredits`);
  const getPostScore = async (postId: number): Promise<PostScore> => get(`/posts/${postId}/score`);
  const getProjectsForPost = async (postId: number): Promise<Project[]> => get(`/posts/${postId}/projects`);
  const enqueueStoriesImport = async (account: string): Promise<void> => get('/posts/enqueueStoriesImport', { account });
  const updatePostOwner = async (postId: number, userId: number): Promise<Post> => post('/posts/owner', { postId, userId });

  // ----- api/stripe
  const getStripeSetup = async (): Promise<{ publishableKey: string }> => get('/stripe/setup');
  const createStripeCheckout = async (coupon?: string, promocode?: string, isResuming?: boolean): Promise<StripeSessionResponse> => post('/stripe/checkout', { coupon, promocode, isResuming });
  const createStripePortal = async (returnTo: string): Promise<StripeSessionResponse> => post('/stripe/portal', { returnTo });
  const giveFreeMonth = async (userId: number) => post('/stripe/giveFreeMonth', { userId });
  const endTrialEarly = async (): Promise<Subscription> => get('/users/subscription/endTrial');

  // ----- api/users
  const createUser = async (user: Partial<User>): Promise<User> => post('/users', user);
  const listAllUsers = async (params: any): Promise<AdminViewUserResponse> => get('/users/admin/search', params, { returnFullJson: true });
  const getUserById = async (userId: number): Promise<User> => get(`/users/${userId}`);
  const getUserByUsername = async (username: string): Promise<User> => get(`/users/username/${username}`);
  const getUserByPostId = async (postId: number): Promise<User> => get(`/users/post/${postId}`);
  const suggestUsersForPost = async (postId: number, creditType: CreditType): Promise<User[]> => get(`/users/forPost/${postId}`, { creditType });
  const getSuggestedUserToCredit = async (postId: number): Promise<User[]> => get(`/users/credits/post/${postId}`);
  const getSuggestedCreditCountByUserId = async (userId: number): Promise<{ count: number }> => get(`/users/suggestedCount/${userId}`);

  /** Update your own user */
  const updateOwnUser = async (user: Partial<User>): Promise<User> => {
    if (!currentUser) {
      throw new Error('Must be logged in to update your user');
    } else {
      return put(`/users/${currentUser.id}`, user);
    }
  };

  /** Update another user you have permission to update */
  const updateOtherUser = async (userId: number, user: Partial<User>): Promise<User> => put(`/users/${userId}`, user);
  const updateUserViaAdmin = async (user: Partial<User>) => put(`/users/admin/${user.id}`, user, { returnFullJson: true });
  const claimUserReferral = async (referralCode: string) => post(`/users/claimReferral/${referralCode}`);
  const getUserByReferralCode = async (referralCode: string): Promise<User> => get(`/users/byReferralCode/${referralCode}`);
  const createFeedback = async (feedback: FeedbackType) => post('/users/feedback', feedback);
  const getUserSettings = async (): Promise<UserSettings> => get('/userSettings');
  const updateUserSettings = async (settings: Partial<UserSettings>): Promise<UserSettings> => put('/userSettings', settings);
  const getTwitterUser = async (query: string): Promise<NetworkUser> => get(`/twitterProxy/${query}`);
  const searchUsers = async (query: string, postId?: number): Promise<User[]> => get('/users/search', {
    query,
    postId,
  });
  const searchUsersForTeamMember = async (query: string): Promise<User[]> => get('/users/search/forTeamMember', { query });
  const updateUsername = async (username: string): Promise<User> => get('/users/username', { username });
  const updateAvatar = async (userId: number, fileData: FormData, filename: string): Promise<User> => putFile(`/users/update_avatar/${userId}?filename=${filename}`, fileData);

  const getTwitterFollowers = async (username: string) => get(`/twitterProxy/${username}/topFollowers`);

  const getMyGondolaStats = async (): Promise<MyGondolaResponse> => get('/users/stats/myGondola');

  const searchCreators = async (
    query: CreatorSearchCriteria,
  ): Promise<CreatorSearchESResponse> => get('/creators/search', query, { returnFullJson: true, useAbort: true, customTimeout: THIRTY_SECONDS });
  const searchCreatorsES = async (
    query: CreatorSearchCriteria,
  ): Promise<CreatorSearchESResponse> => get('/creators/search-es', query, { returnFullJson: true, useAbort: true, customTimeout: THIRTY_SECONDS });

  const getCreatorsListDetails = async (
    creatorIds: number[],
  ): Promise<Record<number, { savedToListsWithStages: ListOrProjectWithStages[]; savedToProjectsWithStages: ListOrProjectWithStages[] }>> => post('/creators/listDetails', { creatorIds });

  const getCreatorSearchCriteria = async (
    query: string,
  ): Promise<NaturalLanguageSearchResponse> => get('/creators/criteria', { q: query }, { useAbort: true });
  const featuredCreators = async (
    query: CreatorSearchCriteria,
  ): Promise<CreatorSearchESResponse> => get('/creators/featured', query, { returnFullJson: true, useAbort: true, customTimeout: 1000 });
  const featuredCreatorsES = async (
    query: CreatorSearchCriteria,
  ): Promise<CreatorSearchESResponse> => get('/creators/featured-es', query, { returnFullJson: true, useAbort: true, customTimeout: 1000 });

  const getTestimonials = async (
    query: CreatorSearchCriteria,
  ): Promise<CreatorSearchESResponse> => get('/creators/testimonials', query, { returnFullJson: true, useAbort: true, customTimeout: 500 });

  const exportCreatorsCSVReport = async (query: CreatorSearchCriteria, title: string): Promise<PostSearchResponse> => get('/creators/csv', { ...query, title });

  const topWeeklyCreators = async (roleIds: number[], sort: string, size: number): Promise<CreatorsListUser[]> => get('/creators/weeklytop', { roleIds, sort, size });

  const sendDirectMessage = async (toUserId: number, subject: string, body: string, ccEmail: boolean): Promise<void> => post('/users/message', {
    toUserId,
    body,
    subject,
    ccEmail,
  });
  const getUserNextSteps = async (): Promise<NextStep | undefined> => {
    try {
      const res = await get<Promise<NextStep>>('/users/nextstep');
      return res;
    } catch (e: any) {
      // noop
    }
    return undefined;
  };
  const onboardUser = async (): Promise<void> => post('/users/onboard');
  const triggerWelcomeEmail = async (): Promise<void> => post('/users/welcome');
  const getCollaborators = async (userId: number): Promise<User[]> => get(`/users/${userId}/collaborators`);
  const getAllUserReferrals = async (query: Query): Promise<ReferralsResponse> => get('/admin/referrals', query);
  const checkDeactivateUser = async (userId: number): Promise<DeactivationCheckResponse> => get(`/users/${userId}/deactivation`);
  const deactivateUser = async (userId: number) => httpDelete(`/users/${userId}`);
  const queueReplayRecalc = async (userId: number) => post(`/admin/replayRecalc/${userId}`);
  const saveUserData = async (userData: Partial<UserData>) => post('/users/userData', { userData });
  const getUserHistory = async (userId: number): Promise<History[]> => get(`/admin/history/${userId}`, {}, { useAbort: true });
  const getUsernameAvailability = async (username: string): Promise<AllNetworks> => get(`/users/username/${username}/available`);
  const claimPersonalIgUsername = async (username: string): Promise<User> => get(`/users/username/${username}/claimPersonal`);
  const uploadResume = async (file: FormData, filename: string, folder: string): Promise<Media[]> => postFile(`/users/uploadResume?filename=${filename}&folder=${folder}`, file);
  const mergeUsers = async (keepUserId: number, duplicateUserId: number, keepUsername?: string) => post('/admin/mergeUsers', { keepUserId, duplicateUserId, keepUsername });
  const getProfileStats = async (userId: number): Promise<PostServiceStats> => get(`/users/profileStats/${userId}`);
  const reindexUserPosts = async (userId: number): Promise<void> => post(`/users/${userId}/reindexPosts`);
  const getHeardAboutOptions = async (): Promise<string[]> => get('/users/data/heardabout');
  const getUserTeamInvites = async (): Promise<Team[]> => get('/teams/teamInvites');
  const getTotalFollowers = async (userId: number): Promise<number> => get(`/users/totalFollowers/${userId}`);
  const getUserMetadata = async (userId: number): Promise<Array<{ term: string; count: number }>> => get(`/users/metadata/${userId}`);
  const searchTeamCreators = async (teamId: number, query: CreatorSearchCriteria): Promise<TeamCreatorsResponse> => get(`/teams/${teamId}/creators`, query);
  const createTeamCreators = async (teamId: number, userIds: number[]): Promise<TeamCreator[]> => post(`/teams/${teamId}/creators`, { userIds });
  const getTeamTableConfig = async (teamId: number, tableName: string): Promise<TeamTableConfig> => get(`/teams/${teamId}/tables/${tableName}/config`);
  const updateTeamTableConfig = async (teamId: number, tableName: string, columns: string[]): Promise<TeamTableConfig> => put(`/teams/${teamId}/tables/${tableName}/config`, { columns });
  const updateTeamCreator = async (teamId: number, teamCreatorId: number, data: TeamCreator): Promise<TeamCreator> => put(`/teams/${teamId}/creators/${teamCreatorId}`, data);
  const getProjectApplicants = async (teamId: number, projectId: number, query: SourcingSearchCriteria): Promise<ProjectApplicantsResponse> => get(`/teams/${teamId}/sourcing/${projectId}`, query);
  const getProjectSummariesByProjectIds = async (projectIds: number[]): Promise<ProjectSummary[]> => post('/projects/projectSummaries', { projectIds });
  const addCommentToTeamCreator = async (teamId: number, teamCreatorId: number, comment: string): Promise<TeamComment> => put(`/teams/${teamId}/creators/${teamCreatorId}/comment`, { comment });

  // ----- api/saved reports
  const getSavedReports = async (teamId: number): Promise<SavedReport[]> => get(`/teams/${teamId}/savedReports`);
  const createSavedReport = async (teamId: number, name: string, query: any): Promise<SavedReport> => post(`/teams/${teamId}/savedReports`, { name, query });

  // ----- api/credits
  const createCredits = async (payload: CreateCreditsPayload): Promise<PostingAccount> => post('/credits/bulk', payload);
  const updateCredit = async (creditId: number, credit: EditCredit) => put<Credit>(`/credits/${creditId}`, credit);
  const deleteCredit = async (creditId: number) => httpDelete(`/credits/${creditId}`);
  const reportCredit = async (disputedCredit: DisputedCredit) => post(`/credits/${disputedCredit.creditId}/report`, {
    ...disputedCredit,
    status: DISPUTED_CREDIT_STATUS.open,
  });
  const getCreditCreator = async (creditId: number): Promise<Credit> => get(`/credits/${creditId}/creator`);

  // ----- api/search
  const getTopSearch = async (query: string) => get<SearchResultResponse>('/topsearch', { query }, { useAbort: true });
  const getProfileFilterSearch = async (query: string, userId: number) => get<SearchResultResponse>(`/filters/${userId}`, { query }, { useAbort: true });
  const getDashboardFilterSearch = async (query: string, accountId: number | undefined, teamId: number | undefined) => get<SearchResultResponse>('/filters/dashboard', { query, accountId, teamId }, { useAbort: true });
  const getCreatorsFilterSearch = async (query: string, userTypeId?: number) => get<SearchResultResponse>('/filters/creators', { query, userTypeId }, { useAbort: true });
  const getCollaboratorRolesFilterSearch = async (
    query: string,
  ) => get<SearchResultResponse>('/filters/collaborators', { query }, { useAbort: true });
  const getCreatorMetadataFilter = async (query: string) => get<SearchResultResponse>('/filters/creatorMetadata', { query }, { useAbort: true });

  // ----- auditlogs and event tracking
  const listCreditAuditsForPost = async (postId: number): Promise<Audit[]> => get('/auditLogs/credits', {
    postId,
    size: 100,
  });
  const listTagAuditsForPost = async (postId: number): Promise<Audit[]> => get('/auditLogs/tags', { postId });
  const trackEvent = async (event: TrackedEvent) => post('/track', event);
  const trackEmailPwError = async (email: string, data: object) => post('/track', {
    action: ACTIONS.signinError,
    target: 'AuthEmail',
    targetId: email,
    fields: data,
  });
  const trackGoogleAuthError = async (data: object) => post('/track', {
    action: ACTIONS.signinError,
    target: 'AuthGoogle',
    fields: data,
  });

  // ----- failedposts
  const listFailedPosts = async (query: any): Promise<FailedPostResponse> => get('/failedposts', query, { returnFullJson: true });

  // ------ blog
  const listBlogPosts = async (query: any): Promise<{ data: BlogPost[]; meta: any }> => get('/blog', query);
  const refreshBlogPosts = async () => post('/blog/refresh');

  // ------ notifications
  const getNotificationsStatus = async (): Promise<NotificationStatus> => get('/notifications/status');
  const getNotificationsByUser = async (): Promise<Notification[]> => get('/notifications/user');
  const archiveNotification = async (id: string): Promise<void> => put(`/notifications/${id}/archive`, { status: 'archived' });
  const markNotificationRead = async (id: string): Promise<void> => put(`/notifications/${id}/read`, { status: 'read' });
  const markAllNotificationsRead = async (): Promise<void> => put('/notifications/all/read');

  // ------ postMetrics
  const getAllPostMetricsForPost = async (postId: number): Promise<PostMetrics[]> => get(`/postMetrics/post/${postId}`);
  const updatePostMetricsAndPost = async (postId: number, metrics: PostMetricsUpdate): Promise<Post> => put(`/postMetrics/post/${postId}`, { metrics });

  // ------ matches
  const getMatch = async (matchId: string): Promise<Post[]> => get(`/matches/${matchId}`);
  const getAllMatchesAdmin = async (query: MatchAdminQuery): Promise<MatchResponse> => get('/matches/admin', query, { useAbort: true });
  const addPostsToMatch = async (postIds: number[], matchId?: string, mediaComparisonResultId?: number): Promise<MatchPost[]> => post('/matches', { postIds, matchId, mediaComparisonResultId });
  const deleteMatchPost = async (matchPostId: string): Promise<boolean | undefined> => httpDelete(`/matches/${matchPostId}`);
  const unflagMatch = async (matchId: string) => post(`/matches/${matchId}/unflag`);
  const mergeMatches = async (matchId: string, matchIds: string[]) => post(`/matches/${matchId}/merge`, { matchIds });
  const getMatchStatsDifference = async (matchId: string): Promise<Stats | undefined> => get(`/matches/${matchId}/statDifference`);
  const copyOriginalPostCreditsToAll = async (matchId: string, postId: number) => post(`/matches/${matchId}/copyCredits`, { postId });
  const getMatchMetrics = async (matchId: string): Promise<MatchMetrics> => get(`/matches/${matchId}/metrics`);

  // ------ filter posts
  const getExplorePosts = async (query?: PostSearchCriteria): Promise<PostSearchResponse> => get('/posts/explore', query, { returnFullJson: true, useAbort: true });
  const getManagerPosts = async (query?: PostSearchCriteria): Promise<PostSearchResponse> => get('/posts/manager', query, { returnFullJson: true, useAbort: true });
  const getProfilePosts = async (profileUserId: number, query?: PostSearchCriteria): Promise<PostSearchResponse> => get(`/posts/profile/${profileUserId}`, query, { returnFullJson: true, useAbort: true });
  const getAnalyticsPosts = async (query?: PostSearchCriteria): Promise<PostSearchResponse> => get('/posts/analytics', query, { returnFullJson: true, useAbort: true });
  const getPinnedPosts = async (profileUserId: number): Promise<Pin[]> => get(`/posts/profile/${profileUserId}/pinned`);
  const getPostSearchCriteria = async (query: string): Promise<NaturalLanguageSearchResponse> => get('/posts/criteria', { q: query }, { useAbort: true });
  const exportCSVReportForProfile = async (profileUserId: number, query?: PostSearchCriteria): Promise<void> => get('/posts/report', { ...query, profileUserId });
  const exportCSVReportForExplore = async (title: string, query?: PostSearchCriteria): Promise<void> => get('/posts/report', { ...query, title });

  const getPostVectorViaText = async (text: string, from?: number): Promise<PostSearchResponse> => get('/postVector', { text, from }, { returnFullJson: true });

  // ------ analytics
  const getGlobalDashboard = async (query?: AnalyticsCriteria): Promise<GlobalAnalyticsResponse> => get('/analytics/dashboard/global', query, { customTimeout: THIRTY_SECONDS });
  const getStreamlinedDashboard = async (query?: AnalyticsCriteria): Promise<StreamlinedAnalyticsResponse> => get('/analytics/dashboard/streamlined', query, { customTimeout: THIRTY_SECONDS });
  const getProfileDashboard = async (query?: AnalyticsCriteria): Promise<GlobalAnalyticsResponse> => get('/analytics/dashboard/profile', query, { customTimeout: THIRTY_SECONDS });
  const getTimeSeriesAnalytics = async (postIds: number[], criteria?: AnalyticsCriteria): Promise<GroupedTimeSeriesResponse | { status: 'processing'; message: string }> => post('/analytics/dashboard/timeSeries', { postIds, criteria }, { customTimeout: THIRTY_SECONDS });
  const getFollowerAnalytics = async (userIds: number[], criteria?: AnalyticsCriteria): Promise<UserWithFollowerDelta[]> => post('/analytics/dashboard/followers', { userIds, criteria }, { customTimeout: THIRTY_SECONDS });
  const setDashboardChatContext = async (payload: DashboardChatContextPayload): Promise<DashboardChatContextResponse> => post('/analytics/dashboard/chat/context', payload);
  const askDashboardChat = async (payload: DashboardChatRequest): Promise<DashboardChatEnqueueResponse> => post('/analytics/dashboard/chat', payload, { customTimeout: THIRTY_SECONDS });
  const getDashboardChatStatus = async (jobId: string): Promise<DashboardChatStatusResponse> => post('/analytics/dashboard/chat/status', { jobId }, { customTimeout: THIRTY_SECONDS });
  // ------ similar posts/images
  const getSimilarImages = async (query: SimilarImagesQuery): Promise<SimilarImageResponse> => get('/admin/similarImages', query, { useAbort: true });
  const ignoreSimilarImage = async (id: string): Promise<void> => put(`/admin/similarImages/ignore/${id}`);
  const matchSimilarImage = async (id: string): Promise<void> => put(`/admin/similarImages/match/${id}`);
  const getSimilarPosts = async (postId: number): Promise<Post[]> => get(`/posts/${postId}/similar`);
  const arePostsMatchworthy = async (postId: number, compareId: number[]): Promise<boolean> => get(`/matches/score/${postId}`, { compareId });
  const generateSimilarImages = async (postId: number): Promise<number> => post('/admin/similarImages', { postId });

  // ------ authed twitter
  const getAvatarFromTwitter = async (username: string): Promise<string> => get(`/twitterProxy/${username}/avatar`);
  const addTwitterProfile = async (username: string) => post('/admin/twitterProfile', { username });

  // ------ authed instagram
  const addInstagramAccount = async (username: string) => put('/admin/instagramAccount', { username });

  // ------ authed tiktok
  const addTiktokProfile = async (username: string) => post('/admin/tiktokProfile', { username });
  const revokeTikTokAuth = async (networkAccountId: number) => post(`/auth/tiktok/revoke/${networkAccountId}`);

  // ------ authed youtube
  const addYoutubeProfile = async (username: string) => put('/admin/youtubeProfile', { username });

  // ------ authed twitch
  const addTwitchProfile = async (username: string) => put('/admin/twitchProfile', { username });
  const revokeTwitchAuth = async (networkAccountId: number) => post(`/auth/twitch/revoke/${networkAccountId}`);

  // ------ authed threads
  const revokeThreadsAuth = async (networkAccountId: number) => post(`/auth/threads/revoke/${networkAccountId}`);

  // ------ authed facebook
  const revokeFacebookAuth = async () => post('/auth/facebook/revoke');

  // ------ posting accounts
  const getPostingAccounts = async (): Promise<PostingAccount[]> => get('/users/postingAccounts');
  const addPostingAccount = async (account: Partial<PostingAccount>): Promise<PostingAccount> => post('/users/postingAccounts', account);
  const addPostingAccounts = async (accounts: Partial<PostingAccount>[], userId: number): Promise<PostingAccount[]> => post(`/users/postingAccounts/bulk/${userId}`, accounts);
  const deletePostingAccount = async (id: number) => httpDelete(`/users/postingAccounts/${id}`);
  const updatePostingAccount = async (id: number, account: Partial<PostingAccount>): Promise<PostingAccount> => put(`/users/postingAccounts/${id}`, account);
  const syncSocialLinkForPostingAccount = async (postingAccountId: number): Promise<User> => post(`/users/postingAccounts/${postingAccountId}/socialLink`);
  const deleteSocialLinkViaPostingAccount = async (postingAccountId: number): Promise<User> => httpDelete(`/users/postingAccounts/${postingAccountId}/socialLink`);
  const findByNetwork = async (networkId: number, username: string): Promise<NetworkUser> => get(`/users/postingAccounts/findByNetwork/${networkId}`, { username });
  const lookUpAllNetworks = async (username: string, includeExistingUser: boolean, ownedAccountsUserId?: number): Promise<AllNetworks> => get('/users/postingAccounts/lookUpAllNetworks', { username, includeExistingUser, ownedAccountsUserId });
  const checkForMatchingAccount = async (username: string): Promise<boolean> => put(`/users/postingAccounts/checkForMatchingAccount/${username}`);
  const enqueueHistoricalImportAdmin = async (userId: number, networkId?: number) => post('/admin/enqueueHistoricalImport', { networkId, userId });
  const enqueueBulkAddPostingAccounts = async (rows: string[], listName: string, roleId?: number, teamId?: number) => post('/users/postingAccounts/import', {
    rows, listName, roleId, teamId,
  });
  const listTeamManagedPostingAccounts = async (accountId?: number): Promise<PostingAccount[]> => get('/users/postingAccounts/teamAccounts', { accountId });
  const adminPostingAccounts = async (query: PostingAccountCriteria): Promise<PostingAccountsSearchResponse> => get('/admin/listPostingAccountsForAdmin', query);
  const addLinkedInPostingAccount = async (url: string, userId: number): Promise<PostingAccount> => post('/users/postingAccounts/linkedin', { url, userId });

  // ------ network accounts
  const autoImportOn = async (account: string, network: number) => post(`/networkAccounts/${account}/${network}/autoImport`);
  const autoImportOff = async (account: string, network: number) => httpDelete(`/networkAccounts/${account}/${network}/autoImport`);
  const searchNetworkAccounts = async (query: PostingAccountCriteria): Promise<NetworkAccountsSearchResponse> => get('/networkAccounts', query);
  const updateNetworkAccountById = async (networkAccountId: number): Promise<NetworkAccount> => post(`/networkAccounts/${networkAccountId}/refresh`);

  // ------ user default resume
  const updateDefaultResume = async (resumeUrl: string): Promise<User> => put('/users/resumes', { resumeUrl });
  const clearResume = async (): Promise<User> => httpDelete('/users/resumes');
  const getResumeSignedUrl = async (userId: number, applicationId?: number): Promise<{ signedUrl: string }> => get(`/users/resumes/${userId}/signedUrl`, applicationId ? { applicationId } : undefined);

  // ------ teams
  const addTeam = async (team: Team): Promise<Team> => post('/teams', team);
  const updateTeam = async (id: number, team: Partial<Team>): Promise<Team> => put(`/teams/${id}`, team);
  const getTeams = async (): Promise<Team[]> => get('/teams');
  const getTeamsAdmin = async (query: AdminTeamQueryParams): Promise<AdminTeamsResponse> => get('/teams/admin', query, { returnFullJson: true });
  const addMember = async (teamId: number, inviteMember: Partial<TeamMember>): Promise<TeamMember> => post(`/teams/${teamId}/members/invite`, inviteMember);
  const deleteMember = async (teamId: number, teamMemberId: number): Promise<TeamMember> => httpDelete(`/teams/${teamId}/members`, { teamMemberId });
  const updateMember = async (teamId: number, memberId: number, member: Partial<TeamMember>): Promise<TeamMember> => put(`/teams/${teamId}/members/${memberId}`, member);
  const addTeamAccount = async (teamId: number, accountId: number): Promise<User> => post(`/teams/${teamId}/accounts`, { accountId });
  const deleteTeamAccount = async (teamId: number, accountId: number): Promise<User> => httpDelete(`/teams/${teamId}/accounts`, { accountId });
  const getTeamAccounts = async (teamId: number): Promise<TeamAccount[]> => get(`/teams/${teamId}/accounts`);
  const updateAccountSettings = async (teamId: number, accountId: number, settings: TeamAccountSettings) => put(`/teams/${teamId}/accounts`, { accountId, settings });
  const updateTeamAccountSettings = async (teamId: number, accountId: number, settings: TeamAccountSettings) => put(`/teams/${teamId}/accountSettings`, { accountId, settings });
  const resendTeamInvite = async (teamMemberId: number) => post(`/teams/${teamMemberId}/reinvite`);
  const ackTermsForAccount = async (teamId: number, accountId: number) => post(`/teams/${teamId}/acknowledge`, { accountId });
  const isAccountAlreadyManaged = async (accountId: number): Promise<boolean> => get(`/teams/accounts/${accountId}`);
  const toggleWorkForUs = async (teamId: number, account: Partial<TeamAccount>) => post(`/teams/${teamId}/workForUs`, account);
  const getTeamApiPermissions = async (teamId: number): Promise<ExternalApiPermission> => get(`/teams/${teamId}/permissions`);
  const deleteTeam = async (teamId: number) => httpDelete(`/teams/${teamId}`); // admin only
  const getAllTeamAccounts = async (): Promise<TeamAccount[]> => get('/admin/teamAccounts'); // admin only
  const requestToJoinTeam = async (teamId: number) => post(`/teams/${teamId}/members/request`);

  const getTeamStripeSession = async (teamId: number, successUrl: string, cancelUrl: string): Promise<StripeSessionResponse> => get(`/teams/${teamId}/session`, { successUrl, cancelUrl });
  const getTeamStripeCustomer = async (teamId: number): Promise<StripeCustomerResponse> => get(`/teams/${teamId}/stripeCustomer`);
  const getTeamSetupIntent = async (teamId: number): Promise<Stripe.SetupIntent> => get(`/teams/${teamId}/setupIntent`);

  const getStripePaymentMethods = async (teamId: number): Promise<StripePaymentMethod[]> => get(`/teams/${teamId}/paymentMethods`);
  const deleteStripePaymentMethod = async (teamId: number, paymentMethodId: string) => httpDelete(`/teams/${teamId}/paymentMethods`, { paymentMethodId });

  const getTeamInvoices = async (teamId: number): Promise<TeamSubscriptionInvoice[]> => get(`/teams/${teamId}/invoices`);
  const getTeamBillingInfo = async (teamId: number): Promise<TeamBillingInfo> => get(`/teams/${teamId}/subscriptions/billing`);
  const addApplicationRecipient = async (teamId: number, memberId: number) => put(`/teams/${teamId}/addApplicationRecipient/${memberId}`);
  const removeApplicationRecipient = async (teamId: number, memberId: number) => put(`/teams/${teamId}/removeApplicationRecipient/${memberId}`);
  const getTeamAccountImports = async (teamId: number) => get(`/teams/${teamId}/imports`);
  const scrapeAsap = async (teamId: number, accountId: number) => post(`/teams/${teamId}/scrapeAsap/${accountId}`);
  const getJobsByAccountIdForTeams = async (teamId: number, accountId: number): Promise<ScraperAccount> => get(`/teams/${teamId}/importJobs/${accountId}`);
  const getExternalJob = async (link: string): Promise<Job> => get('/jobs/external', { link });
  const createAdminCheckout = async (teamId: number, plan: string, periodType: string, trialPeriodDays?: number): Promise<{ url: string }> => post('/stripe/admin/checkout', {
    teamId, plan, periodType, trialPeriodDays,
  });

  // ------ creator stages
  const getCreatorStages = async (teamId: number): Promise<CreatorStage[]> => get(`/teams/${teamId}/creatorStages`);
  const createCreatorStage = async (teamId: number, stage: Partial<CreatorStage>): Promise<CreatorStage> => post(`/teams/${teamId}/creatorStages`, stage);
  const updateCreatorStage = async (teamId: number, stage: Partial<CreatorStage>): Promise<CreatorStage> => put(`/teams/${teamId}/creatorStages`, stage);
  const deleteCreatorStage = async (teamId: number, creatorStageId: number): Promise<void> => httpDelete(`/teams/${teamId}/creatorStages/${creatorStageId}`);

  // ------ milestones
  const getAllMilestones = async (): Promise<AdminMilestoneResponse> => get('/milestones');
  const addMilestone = async (milestone: Partial<Milestone>) => post('/milestones', milestone);
  const deleteMilestone = async (milestoneId: number) => httpDelete(`/milestones/${milestoneId}`);
  const updateMilestone = async (milestoneId: number, milestone: Partial<Milestone>) => put(`/milestones/${milestoneId}`, milestone);
  const getUserMilestones = async (userId: number): Promise<Milestone[]> => get(`/milestones/users/${userId}`);
  const getUserStreaks = async (userId: number) => get(`/milestones/streaks/${userId}`);
  const getAchievedMilestoneCount = async (userId: number): Promise<{ achievedCount: number; totalCount: number }> => get(`/milestones/achieved/${userId}`);
  const getMyGondolaMilestones = async (userId: number): Promise<MyGondolaMilestones> => get(`/milestones/myGondola/${userId}`);
  const getTopNewMilestoneForUser = async (): Promise<Milestone> => get('/milestones/newest');

  // ------ post imports
  const importUrls = async (postURLs: string[], importName: string, projectId?: number) => post('/posts/import', { postURLs, importName, projectId });
  const importPosts = async (posts: PostToImport[], importName: string): Promise<Track> => post('/bulk/importPosts', { posts, importName });

  // ------ scrape statues
  const getAllScraperAccounts = async (query?: ScraperAdminCriteria): Promise<ScraperStatusResponse> => get('/admin/scrapers/accounts', query);
  const updateScrapePriority = async (accountId: number, priority: string) => put(`/admin/scrapers/${accountId}`, { priority });
  const getNetworkConfig = async (): Promise<NetworkStatus[]> => get('/admin/networkConfig');
  const updateNetworkConfig = async (networkId: number, status: boolean) => put(`/admin/networkConfig/${networkId}`, { status });
  const getAllJobsByAccountId = async (accountId: number): Promise<ScraperAccount> => get(`/admin/scrapers/${accountId}/jobs`);
  const getTeamScraperJobs = async (teamId: number): Promise<any> => get(`/admin/scrapers/teams/${teamId}`);

  // ------ auth/google
  const signInWithGoogle = async (payload: GoogleAuthPayload): Promise<void> => post('/auth/google/signin', payload);
  const checkCaptcha = async (token: string): Promise<any> => post('/auth/google/captcha', { token });

  // ------ auth/apple
  const signInWithApple = async (payload: AppleWebSignInCredential): Promise<void> => post('/auth/apple/signin/web', payload);

  /**
   * Send the code returned by Google login to the backend to see if the email address is in use
   * and return idToken and their authStatus.
   */
  const exchangeTokens = async (code: string): Promise<TokenExchangeResponse> => post('/auth/google/tokens', { code });

  // ------ auth/email
  const signUpWithEmail = async (payload: EmailCredential): Promise<void> => post('/auth/email/signup', payload);
  const saveUsername = async (username: string): Promise<void> => put('/users/username', { username });
  const signInWithEmail = async (payload: EmailCredential): Promise<void> => post('/auth/email/signin', payload);
  const signInWithMagicToken = async (magicToken: string): Promise<void> => post('/auth/email/signin/magic', magicToken);
  const getCredential = async (): Promise<EmailCredential> => get('/auth/email');
  const checkUncredentialedEmail = async (email: string): Promise<CredentialsForEmail> => get('/auth/email/check', { email });
  const sendVerifyCode = async (email: string, returnTo: string): Promise<void> => put('/users/verify', { email, returnTo });
  const sendMagicLinkEmail = async (email: string, returnTo: string): Promise<void> => post('/auth/email/magicLink', { email, returnTo });
  const verifyCode = async (email: string, code: string): Promise<void> => post('/users/verify', { email, code });

  // ------ replay
  const getAllReplayCards = async (query: any) => get('/admin/getAllReplayCards', query);

  // ------ lotties
  const getMilestoneCelebrate = async () => get('/lotties/milestone/celebrate');

  // ------ lists
  const addList = async (list: AddList): Promise<List> => post('/lists', list);
  const addUserToList = async (listId: number, userId: number, source?: string) => post(`/lists/${listId}/user`, { userId, source });
  const removeUserFromList = async (listId: number, userId: number) => httpDelete(`/lists/${listId}/user`, { userId });
  const editList = async (list: List): Promise<List> => put(`/lists/${list.id}`, list);
  const deleteList = async (listId: number) => httpDelete(`/lists/${listId}`);
  const getUserLists = async (includeUsers?: boolean): Promise<List[]> => get('/lists', { includeUsers });
  const bulkAddUsersToList = async (users: User[], listId: number) => post(`/lists/${listId}/users`, { users });
  const addVote = async (listId: number, userId: number): Promise<ListVote> => post(`/lists/${listId}/vote`, { userId });
  const removeVote = async (listId: number, listVoteId: number) => httpDelete(`/lists/${listId}/vote/${listVoteId}`);
  const updateListUserStage = async (listId: number, listUserId: number, creatorStageId: number): Promise<List> => put(`/lists/${listId}/user/${listUserId}`, { creatorStageId });
  const getSampleList = async (): Promise<List> => get('/lists/sample');
  const getPublicList = async (listId: string): Promise<List> => get(`/lists/${listId}`);
  const getTeamLists = async (teamId: number): Promise<List[]> => get(`/lists/teams/${teamId}`);
  const addCommentToListUser = async (listId: number, userId: number, comment: string): Promise<any> => post(`/lists/${listId}/comment`, { userId, comment });
  const deleteListComment = async (listId: number, commentId: number): Promise<void> => httpDelete(`/lists/${listId}/comment/${commentId}`);
  const editListComment = async (listId: number, commentId: number, comment: string): Promise<any> => put(`/lists/${listId}/comment/${commentId}`, { comment });
  const getCommentsForUserByProject = async (userId: number, projectId: number): Promise<ListUser[]> => get(`/lists/comments/${userId}/forProject/${projectId}`);

  // ------ credentials
  const updateCredential = async (credentialId: number, credential: EmailCredential): Promise<EmailCredential> => put(`/users/credentials/${credentialId}`, credential);

  // ------ connections
  const getConnections = async (): Promise<Connection[]> => get('/users/connections');
  const deleteConnection = async (connectionId: number) => httpDelete(`/users/connections/${connectionId}`);

  // ------ jobs
  const getAllJobs = async (query: any): Promise<JobSearchResponse> => get('/jobs', query);
  const getJob = async (jobId: number): Promise<Job> => get(`/jobs/${jobId}`);
  const addJob = async (job: JobSchemaType): Promise<Job> => post('/jobs', job);
  const updateJob = async (jobId: number, job: Partial<JobSchemaType>): Promise<Job> => put(`/jobs/${jobId}`, job);
  const addJobApplication = async (jobId: number, application: NewApplication): Promise<User> => post(`/jobs/${jobId}/application`, application);
  const getJobApplication = async (jobId: number): Promise<JobApplication> => get(`/jobs/${jobId}/application`);
  const deleteJobApplication = async (jobId: number): Promise<JobApplication> => httpDelete(`/jobs/${jobId}/application`);
  const getJobsByAccount = async (accountId: number): Promise<Job[]> => get(`/jobs/byAccount/${accountId}`);
  const deleteJob = async (jobId: number): Promise<JobApplication> => httpDelete(`/jobs/${jobId}`);
  const getApplicationById = async (applicationId: number): Promise<JobApplication> => get(`/jobs/applications/${applicationId}`);
  const getFeaturedJobs = async (query: any): Promise<Job[]> => get('/jobs/featured', query);
  const getTeamJobs = async (teamId: number, query?: any): Promise<JobSearchResponse> => get(`/teams/${teamId}/jobs`, query);
  const getJobStatsByTeam = async (teamId: number): Promise<JobStats[]> => get(`/jobs/${teamId}/stats`);
  const getJobSource = async (jobId: number): Promise<{ id: number; source: string; createdBy: string }> => get(`/jobs/${jobId}/source`);

  // ------ projects
  const getProjects = async (teamId?: number): Promise<Project[]> => get('/projects', { teamId });
  const addProject = async (project: Partial<Project>): Promise<Project> => post('/projects', project);
  const updateProject = async (projectId: number, project: Partial<Project>): Promise<Project> => put(`/projects/${projectId}`, project);
  const getProject = async (projectId: number): Promise<Project> => get(`/projects/${projectId}`);
  const getProjectByPublicId = async (publicId: string): Promise<Project> => get(`/projects/public/${publicId}`);
  const addProjectClient = async (projectId: number, clientId: number): Promise<Project> => post(`/projects/${projectId}/clients`, { id: clientId });
  const addConcept = async (concept: Partial<Concept>): Promise<Concept> => post('/concepts', concept);
  const updateConcept = async (conceptId: number, concept: Partial<Concept>): Promise<Concept> => put(`/concepts/${conceptId}`, concept);
  const addConceptMedia = async (conceptId: number, mediaId: number): Promise<Concept> => post(`/concepts/${conceptId}/media`, { id: mediaId });
  const removeConceptMedia = async (conceptId: number, mediaId: number): Promise<Concept> => httpDelete(`/concepts/${conceptId}/media/${mediaId}`);
  const searchConcepts = async (criteria: any): Promise<ConceptSearchResults> => get('/concepts', criteria, { returnFullJson: true });
  const deleteConcept = async (conceptId: number) => httpDelete(`/concepts/${conceptId}`);
  const addConceptTag = async (conceptId: number, value: string): Promise<TagValue[]> => post(`/concepts/${conceptId}/tags`, { value });
  const removeConceptTag = async (conceptId: number, tagId: number): Promise<TagValue[]> => httpDelete(`/concepts/${conceptId}/tags/${tagId}`);
  const deleteProject = async (projectId: number) => httpDelete(`/projects/${projectId}`);
  const searchProjects = async (criteria: any): Promise<ProjectSearchResults> => get('/projects/search', criteria, { returnFullJson: true });
  const addConceptLink = async (conceptId: number, newLink: Partial<Link>): Promise<Link> => post(`/concepts/${conceptId}/links`, newLink);
  const removeConceptLink = async (conceptId: number, linkId: number): Promise<Concept> => httpDelete(`/concepts/${conceptId}/links/${linkId}`);
  const duplicateConcept = async (conceptId: number, projectId?: number, projectName?: string): Promise<Concept> => post(`/concepts/${conceptId}/copy`, { projectId, projectName });
  const generateImage = async (conceptId: number): Promise<OpenAIImagesResponse> => get(`/concepts/${conceptId}/generate`);
  const addLinksToConcepts = async (conceptIds: number[], links: Partial<Link>[]): Promise<Link> => post('/concepts/links/bulk', { conceptIds, links });
  const addGeneratedImage = async (imageUrls: string[], conceptId: number): Promise<Media[]> => post(`/concepts/${conceptId}/addGeneratedImage`, imageUrls);
  const getProjectPostStats = async (projectId: number): Promise<{ stats: Stats; collaborators: User[] }> => get(`/projects/${projectId}/stats`);
  const getUsersFeaturedProjects = async (userId: number): Promise<Project[]> => get(`/projects/portfolio/${userId}`);
  const getFeaturedProjectsForHome = async (): Promise<Project[]> => get('/projects/featured');
  const addCollaboratorToProject = async (projectId: number, user: Partial<ProjectCollaborator>): Promise<Project> => post(`/projects/${projectId}/collaborator`, user);
  const updateProjectCollaborator = async (projectId: number, userId: number, collaboratorData: Partial<ProjectCollaborator>): Promise<ProjectCollaborator> => put(`/projects/${projectId}/collaborator/${userId}`, collaboratorData);
  const deleteProjectCollaborator = async (projectId: number, userId: number): Promise<void> => httpDelete(`/projects/${projectId}/collaborator/${userId}`);
  const getProjectTagSuggestions = async (q: string, teamId?: number, ownerId?: number): Promise<string[]> => get('/projects/tags/suggest', { q, teamId, ownerId });
  const getProjectCollaborators = async (projectId: number): Promise<ProjectCollaborator[]> => get(`/projects/${projectId}/collaborators`);
  const getSuggestedProjectPosts = async (projectId: number): Promise<Post[]> => get(`/projects/${projectId}/suggestedPosts`);
  const addPostsToProject = async (projectId: number, postIds: number[]) => post(`/projects/${projectId}/posts`, { postIds });
  const removePostFromProject = async (projectId: number, postId: number) => httpDelete(`/projects/${projectId}/posts/${postId}`);

  // Team Project Roles methods
  const getTeamProjectRoles = async (projectId: number): Promise<ProjectRole[]> => get(`/teamProjects/${projectId}/teamProjectRoles`);
  const createTeamProjectRole = async (projectId: number, data: Partial<ProjectRole>): Promise<ProjectRole> => post(`/teamProjects/${projectId}/teamProjectRoles`, data);
  const updateTeamProjectRole = async (projectId: number, roleId: number, data: Partial<ProjectRole>): Promise<ProjectRole> => put(`/teamProjects/${projectId}/teamProjectRoles/${roleId}`, data);
  const deleteTeamProjectRole = async (projectId: number, roleId: number): Promise<void> => httpDelete(`/teamProjects/${projectId}/teamProjectRoles/${roleId}`);

  // Budget Estimate methods
  const getBudgetEstimates = async (projectId: number, roleId: number): Promise<BudgetEstimate[]> => get(`/teamProjects/${projectId}/teamProjectRoles/${roleId}/budgetEstimates`);
  const addBudgetEstimate = async (projectId: number, roleId: number, data: Partial<BudgetEstimate>): Promise<BudgetEstimate> => post(`/teamProjects/${projectId}/teamProjectRoles/${roleId}/budgetEstimates`, data);
  const updateBudgetEstimate = async (projectId: number, roleId: number, estimateId: number, data: Partial<BudgetEstimate>): Promise<BudgetEstimate> => put(`/teamProjects/${projectId}/teamProjectRoles/${roleId}/budgetEstimates/${estimateId}`, data);
  const deleteBudgetEstimate = async (projectId: number, roleId: number, estimateId: number): Promise<void> => httpDelete(`/teamProjects/${projectId}/teamProjectRoles/${roleId}/budgetEstimates/${estimateId}`);

  // Candidate methods
  const getProjectCandidates = async (projectId: number): Promise<Candidate[]> => get(`/teamProjects/${projectId}/candidates`);
  const addCandidate = async (projectId: number, data: Partial<Candidate>): Promise<Candidate> => post(`/teamProjects/${projectId}/candidates`, data);
  const updateCandidate = async (projectId: number, candidateId: number, data: Partial<Candidate>): Promise<Candidate> => put(`/teamProjects/${projectId}/candidates/${candidateId}`, data);
  const changeCandidateStage = async (projectId: number, candidateId: number, creatorStageId: number): Promise<Candidate> => post(`/teamProjects/${projectId}/candidates/${candidateId}/stage`, { creatorStageId });
  const rejectCandidate = async (projectId: number, candidateId: number, rejectionReason: string): Promise<Candidate> => post(`/teamProjects/${projectId}/candidates/${candidateId}/reject`, { rejectionReason });

  // Contract methods
  const getContracts = async (projectId: number): Promise<any[]> => get(`/teamProjects/${projectId}/contracts`);
  const getContract = async (projectId: number, contractId: number): Promise<any> => get(`/teamProjects/${projectId}/contracts/${contractId}`);
  const getContractForCandidate = async (projectId: number, candidateId: number): Promise<any | null> => get(`/teamProjects/${projectId}/candidates/${candidateId}/contract`);
  const createContract = async (projectId: number, candidateId: number, data: { rateItems: Array<any> }): Promise<any> => post(`/teamProjects/${projectId}/candidates/${candidateId}/contract`, data);
  const updateContract = async (projectId: number, contractId: number, data: Partial<any>): Promise<any> => put(`/teamProjects/${projectId}/contracts/${contractId}`, data);

  // Project collaborator methods
  const addProjectCollaborator = async (projectId: number, data: { userId: number; projectRoleId?: number | null }): Promise<any> => post(`/projects/${projectId}/collaborator`, data);

  // Project collaborator deliverables methods
  const getProjectCollaboratorDeliverables = async (projectId: number, userId: number): Promise<Deliverable[]> => get(`/projects/${projectId}/collaborator/${userId}/deliverables`);
  const updateProjectCollaboratorDeliverables = async (projectId: number, userId: number, deliverables: Deliverable[]): Promise<void> => put(`/projects/${projectId}/collaborator/${userId}/deliverables`, { deliverables });
  const addProjectCollaboratorDeliverable = async (projectId: number, userId: number, deliverable: Deliverable): Promise<any> => post(`/projects/${projectId}/collaborator/${userId}/deliverables`, deliverable);
  const updateProjectCollaboratorDeliverable = async (projectId: number, userId: number, deliverableId: number, deliverable: Partial<Deliverable>): Promise<any> => put(`/projects/${projectId}/collaborator/${userId}/deliverables/${deliverableId}`, deliverable);
  const deleteProjectCollaboratorDeliverable = async (projectId: number, userId: number, deliverableId: number): Promise<void> => httpDelete(`/projects/${projectId}/collaborator/${userId}/deliverables/${deliverableId}`);
  const enqueueProjectPostImport = async (projectId: number) => get(`/projects/${projectId}/enqueueScrape`);
  const getProjectAnalytics = async (
    publicIds: string[],
    criteria?: PostSearchCriteria,
  ): Promise<ProjectAnalyticsResponse> => {
    const params: any = { projects: publicIds };
    if (criteria) {
      // Merge criteria into params, excluding 'projects' to avoid conflicts
      Object.keys(criteria).forEach((key) => {
        if (key !== 'projects') {
          params[key] = (criteria as any)[key];
        }
      });
    }
    return get('/projects/analytics', params);
  };

  // ------ project cost management
  const getProjectCostSummary = async (projectId: number): Promise<ProjectCostSummary> => get(`/projects/${projectId}/costs`);
  const getCollaboratorCosts = async (projectId: number, userId: number): Promise<CostCalculationResult> => get(`/projects/${projectId}/collaborator/${userId}/costs`);
  const setPostCost = async (projectId: number, postId: number, cost: number | null): Promise<any> => put(`/projects/${projectId}/posts/${postId}/cost`, { cost });
  const getProjectPostCosts = async (projectId: number): Promise<ProjectPostCost[]> => get(`/projects/${projectId}/posts/costs`);

  // ------ additional metrics
  const getProjectPostAdditionalMetrics = async (projectId: number, postId: number): Promise<{ additionalMetrics?: Record<string, number | null>; importIdentifier?: string } | null> => get(`/projects/${projectId}/posts/${postId}/additionalMetrics`);
  const updateProjectPostAdditionalMetrics = async (projectId: number, postId: number, additionalMetrics: Record<string, number | null>, importIdentifier?: string): Promise<any> => put(`/projects/${projectId}/posts/${postId}/additionalMetrics`, { additionalMetrics, importIdentifier });
  const getProjectAdditionalMetricsKeys = async (projectId: number): Promise<string[]> => get(`/projects/${projectId}/posts/additionalMetrics`);
  const importProjectPostCSV = async (projectId: number, csvData: string): Promise<{ success: number; errors: string[] }> => post(`/projects/${projectId}/posts/importCSV`, { csvData });
  const checkMetricForUse = async (projectId: number, metricKey: string): Promise<ProjectPost[]> => get(`/projects/${projectId}/posts/additionalMetrics/${metricKey}`);
  const deleteMetricForProject = async (projectId: number, metricKey: string) => httpDelete(`/projects/${projectId}/posts/additionalMetrics/${metricKey}`);

  // ------ questions
  const getQuestions = async (jobId: number): Promise<ApplicationQuestion[]> => get(`/jobs/${jobId}/questions`);
  const addQuestion = async (jobId: number, question: ApplicationQuestion): Promise<ApplicationQuestion> => post(`/jobs/${jobId}/question`, question);
  const updateQuestion = async (questionId: number, question: ApplicationQuestion): Promise<ApplicationQuestion> => put(`/jobs/questions/${questionId}`, question);
  const deleteQuestion = async (questionId: number) => httpDelete(`/jobs/questions/${questionId}`);

  // ------ auto credits
  const upsertAutoCredit = async (autoCredit: AutoCredit, shouldAutoCreditHistoricalPosts: boolean): Promise<AutoCredit> => post('/users/postingAccounts/autoCredit', { autoCredit, shouldAutoCreditHistoricalPosts });
  const deleteAutoCredit = async (autoCreditId: number) => httpDelete(`/users/postingAccounts/autoCredit/${autoCreditId}`);
  const countAutoCreditsToCreate = async (postingAccountId: number): Promise<AutoCreditCount> => get(`/users/postingAccounts/${postingAccountId}/autoCredit/count`);

  // ------ event signups
  const getEventSignup = async (eventName: string): Promise<EventSignup> => get('/eventSignups', { eventName });
  const addEventSignup = async (data: Partial<EventSignup>): Promise<EventSignup> => post('/eventSignups', data);
  const updateEventSignup = async (id: number, data: EventSignup): Promise<EventSignup> => put(`/eventSignups/${id}`, data);
  const getGSS25Signups = async (query: any): Promise<{ data: EventSignup[] }> => get('/eventSignups/GSS25', query, { returnFullJson: true });

  // ------ worked together
  const getWorkedTogether = async (fromUser: number, toUser: number, randomize?: boolean): Promise<WorkedTogetherResponse> => get('/workedTogether', { fromUser, toUser, randomize });
  const getWorkedTogetherUses = async (): Promise<LimitedUseResponse> => get('/workedTogether/uses');

  // ------ content lists
  const addPostToList = async (listId: number, itemId: number) => post(`/contentLists/${listId}/post`, { itemId });
  const removeItemFromList = async (listId: number, itemId: number) => httpDelete(`/contentLists/${listId}/item/${itemId}`);
  const getProjectLists = async (itemId?: number, itemType?: string): Promise<ContentList[]> => get('/contentLists/projectLists', { itemId, itemType });
  const addItemToList = async (listId: number, itemId: number, itemType: string) => post(`/contentLists/${listId}/item/${itemId}`, { itemType, itemId });
  const addMediaToList = async (listId: number, media: Media): Promise<BTSItem> => post(`/contentLists/${listId}`, media);
  const addLinkToList = async (listId: number, link: Link): Promise<BTSItem> => post(`/contentLists/${listId}/link`, link);
  const getContentListItems = async (listId: number): Promise<BTSItem[]> => get(`/contentLists/${listId}`);
  const updateContentListItem = async (listId: number, btsItem: BTSItem): Promise<BTSItem> => put(`/contentLists/${listId}`, btsItem);
  const bulkAddItemsToList = async (listId: number, itemIds: number[], itemType: string) => post(`/contentLists/${listId}/items`, { itemType, itemIds });

  // ------ linkedin
  const getCurrentUsersPages = async (): Promise<any> => get('/linkedin/pages');

  // ------ caches
  const clearAPICache = async (type: string, key?: Record<string, any>) => post('/admin/caches/clear', { type, key });
  const getAPICaches = async (criteria: CacheSearchCriteria): Promise<ApiCacheResponse> => get('/admin/caches', criteria, { returnFullJson: true });

  // ------ admin
  const sendTestEmail = async (email: string, template: string) => post('/admin/sendTestEmail', { template, email });
  const analyzePost = async (postId: number): Promise<any> => get(`/admin/posts/${postId}/analyze`);
  const getHeardAboutAdminOptions = async (): Promise<HeardAboutOption[]> => get('/admin/heardabout');
  const addHeardAboutOption = async (option: { name: string; weighting: number }) => post('/admin/heardabout', option);
  const deleteHeardAboutOption = async (id: number) => httpDelete(`/admin/heardabout/${id}`);
  const executeNaturalLanguageQuery = async (
    query: string,
    runQuery?: boolean,
  ): Promise<QueryResult> => {
    const payload = runQuery ? { query, runQuery } : { query };
    return post('/admin/reports/query/execute', payload, { customTimeout: THIRTY_SECONDS });
  };
  const explainSQLQuery = async (sql: string): Promise<QueryExplanation> => post('/admin/reports/query/explain', { sql });

  // ------ tubular
  const getAllTubularData = async (query: any): Promise<AdminTubularResponse> => get('/tubular', query);
  const upsertTubularData = async (userId: number): Promise<TubularCache> => post(`/tubular/${userId}`);

  /**
   * ignoreErrors swallows any API errors and returns `defaultReturn` instead
   */
  async function ignoreErrors<T>(
    fnPromise: Promise<T>,
    defaultReturn: T | undefined,
  ) {
    try {
      const users = await fnPromise;
      return users;
    } catch (err) {
      // noop
    }
    return defaultReturn;
  }

  return {
    // TODO(Jordan): Don't return these internal generic functions.
    get,
    put,
    post,
    putFile,
    getCursorPagination,
    delete: httpDelete,

    ignoreErrors,

    // auth
    getAuth,
    token: hasToken(),

    // roles
    listAllRoles,
    listAllRolesAdmin,
    getUserRoles,
    getRoleById,
    createRole,
    updateRole,
    createSuggestedRole,
    getUserHiddenKeyRoles,
    createHiddenKeyRole,
    deleteHiddenKeyRole,
    listAllRoleTags,
    getRoleTagSuggestions,

    // tags
    listTagTypes,
    listAllTagValuesAdmin,
    tagValuesSearch,
    createNewTagValue,
    updateTagValue,
    deleteTagValue,
    createBulkTags,
    listUserTags,
    searchAccountNames,
    suggestTagsForPostId,
    addCreatorTag,
    getCreatorTagsByUser,
    deleteCreatorTag,

    // admin/reports
    listReports,
    getReport,

    // search posts
    getPostById,
    getPostByUrl,

    // posts
    createPinnedPost,
    deletePinnedPost,
    getPinnedPosts,
    createPostByUrl,
    deletePost,
    unDeletePost,
    scrapePostMetrics,
    queueScrapePost,
    addTagToPost,
    deleteTagFromPost,
    reportCredit,
    toggleIsLive,
    toggleStatsPaused,
    toggleIsFeatured,
    toggleSensitiveContent,
    createManualPost,
    uploadMedia,
    uploadCompressedMedia,
    uploadLinkMedia,
    findMatchesForUpload,
    findMatchesForUploadViaImage,
    findMatchesForUploadViaUrl,
    getMediaComparisonResults,
    runMediaComparison,
    getMediaComparisonGroup,
    getMediaComparisonGroups,
    updateMediaComparison,
    updateMediaComparisonGroup,
    updateMediaComparisonGroupCredits,
    createMediaComparisonGroup,
    deleteMediaComparisonGroup,
    addMediaToComparisonGroup,
    deleteComparisonFromComparisonGroup,
    findPostsViaText,
    reindexPost,
    getTrendingPosts,
    getConceptsForPost,
    getCompareUses,
    ignoreSuggestedCredit,
    getPostScore,
    getProjectsForPost,
    enqueueStoriesImport,
    updatePostOwner,

    // stripe
    getStripeSetup,
    createStripeCheckout,
    createStripePortal,
    giveFreeMonth,
    endTrialEarly,

    // users
    createUser,
    listAllUsers,
    getUserById,
    getUserByUsername,
    getUserByPostId,
    updateOwnUser,
    updateOtherUser,
    updateAvatar,
    updateUserViaAdmin,
    claimUserReferral,
    getUserByReferralCode,
    createFeedback,
    getUserSettings,
    updateUserSettings,
    getTwitterUser,
    searchUsers,
    searchUsersForTeamMember,
    searchCreators,
    searchCreatorsES,
    getCreatorsListDetails,
    getCreatorSearchCriteria,
    featuredCreators,
    featuredCreatorsES,
    getTestimonials,
    exportCreatorsCSVReport,
    sendDirectMessage,
    getUserNextSteps,
    triggerWelcomeEmail,
    onboardUser,
    getCollaborators,
    suggestUsersForPost,
    getSuggestedUserToCredit,
    getSuggestedCreditCountByUserId,
    topWeeklyCreators,
    getAllUserReferrals,
    checkDeactivateUser,
    deactivateUser,
    getTwitterFollowers,
    saveUserData,
    updateUsername,
    queueReplayRecalc,
    getUserHistory,
    getUsernameAvailability,
    claimPersonalIgUsername,
    uploadResume,
    mergeUsers,
    getProfileStats,
    getMyGondolaStats,
    reindexUserPosts,
    getHeardAboutOptions,
    getUserTeamInvites,
    getTotalFollowers,
    getUserMetadata,
    searchTeamCreators,
    createTeamCreators,
    updateTeamCreator,
    getProjectApplicants,
    getProjectSummariesByProjectIds,
    getTeamTableConfig,
    updateTeamTableConfig,
    addCommentToTeamCreator,

    // savedReports
    getSavedReports,
    createSavedReport,

    // postImports
    importUrls,
    importPosts,

    // credits
    createCredits,
    updateCredit,
    deleteCredit,
    getCreditCreator,

    // search
    getTopSearch,
    getProfileFilterSearch,
    getCreatorsFilterSearch,
    getCollaboratorRolesFilterSearch,
    getDashboardFilterSearch,
    getCreatorMetadataFilter,

    // auditlogs and event tracking
    listCreditAuditsForPost,
    listTagAuditsForPost,
    trackEvent,
    trackEmailPwError,
    trackGoogleAuthError,

    // failedposts
    listFailedPosts,

    // blog
    listBlogPosts,
    refreshBlogPosts,

    //notifications
    getNotificationsStatus,
    getNotificationsByUser,
    archiveNotification,
    markNotificationRead,
    markAllNotificationsRead,

    //postMetrics
    getAllPostMetricsForPost,
    updatePostMetricsAndPost,

    //matches
    getMatch,
    addPostsToMatch,
    deleteMatchPost,
    getAllMatchesAdmin,
    unflagMatch,
    mergeMatches,
    getMatchStatsDifference,
    copyOriginalPostCreditsToAll,
    getMatchMetrics,

    //filter post
    getExplorePosts,
    getAnalyticsPosts,
    getProfilePosts,
    getManagerPosts,
    getPostSearchCriteria,

    exportCSVReportForProfile,
    exportCSVReportForExplore,

    getPostVectorViaText,

    // analytics
    getGlobalDashboard,
    getStreamlinedDashboard,
    getProfileDashboard,
    getTimeSeriesAnalytics,
    getFollowerAnalytics,
    setDashboardChatContext,
    askDashboardChat,
    getDashboardChatStatus,
    // similar posts/images
    getSimilarPosts,
    getSimilarImages,
    ignoreSimilarImage,
    matchSimilarImage,
    arePostsMatchworthy,
    generateSimilarImages,

    // authed twitter
    getAvatarFromTwitter,
    addTwitterProfile,

    // authed instagram
    addInstagramAccount,

    // authed tiktok
    addTiktokProfile,
    revokeTikTokAuth,

    // authed youtube
    addYoutubeProfile,

    // authed twitch
    addTwitchProfile,
    revokeTwitchAuth,

    // authed threads
    revokeThreadsAuth,

    // authed facebook
    revokeFacebookAuth,

    // posting accounts
    addPostingAccount,
    addPostingAccounts,
    deletePostingAccount,
    updatePostingAccount,
    getPostingAccounts,
    syncSocialLinkForPostingAccount,
    deleteSocialLinkViaPostingAccount,
    findByNetwork,
    lookUpAllNetworks,
    checkForMatchingAccount,
    enqueueHistoricalImportAdmin,
    enqueueBulkAddPostingAccounts,
    listTeamManagedPostingAccounts,
    adminPostingAccounts,
    addLinkedInPostingAccount,

    // network accounts
    searchNetworkAccounts,
    autoImportOn,
    autoImportOff,
    updateNetworkAccountById,

    // default resume
    updateDefaultResume,
    clearResume,
    getResumeSignedUrl,

    // connections
    getConnections,
    deleteConnection,

    // teams
    addTeam,
    getTeams,
    getTeamsAdmin,
    updateTeam,
    addMember,
    deleteMember,
    updateMember,
    addTeamAccount,
    deleteTeamAccount,
    getTeamAccounts,
    updateAccountSettings,
    updateTeamAccountSettings,
    resendTeamInvite,
    ackTermsForAccount,
    isAccountAlreadyManaged,
    toggleWorkForUs,
    getTeamApiPermissions,
    deleteTeam,
    getAllTeamAccounts,
    requestToJoinTeam,

    getTeamStripeSession,
    getTeamStripeCustomer,
    getTeamSetupIntent,
    getStripePaymentMethods,
    deleteStripePaymentMethod,

    getTeamInvoices,
    getTeamBillingInfo,
    addApplicationRecipient,
    removeApplicationRecipient,

    getTeamAccountImports,
    scrapeAsap,
    getJobsByAccountIdForTeams,
    getExternalJob,

    // creator stages
    getCreatorStages,
    createCreatorStage,
    updateCreatorStage,
    deleteCreatorStage,

    // milestones
    getAllMilestones,
    addMilestone,
    deleteMilestone,
    updateMilestone,
    getUserMilestones,
    getUserStreaks,
    getAchievedMilestoneCount,
    getMyGondolaMilestones,
    getTopNewMilestoneForUser,

    // scrape statues
    getAllScraperAccounts,
    getNetworkConfig,
    updateNetworkConfig,
    updateScrapePriority,
    getAllJobsByAccountId,
    getTeamScraperJobs,

    // auth/google
    signInWithGoogle,
    checkCaptcha,
    exchangeTokens,

    // auth/apple
    signInWithApple,

    // auth/email
    signUpWithEmail,
    saveUsername,
    signInWithEmail,
    signInWithMagicToken,
    getCredential,
    checkUncredentialedEmail,
    sendVerifyCode,
    verifyCode,
    sendMagicLinkEmail,

    // replay
    getAllReplayCards,

    // lotties
    getMilestoneCelebrate,

    // lists
    getUserLists,
    addList,
    addUserToList,
    removeUserFromList,
    editList,
    deleteList,
    bulkAddUsersToList,
    addVote,
    removeVote,
    updateListUserStage,
    getSampleList,
    getPublicList,
    getTeamLists,
    addCommentToListUser,
    editListComment,
    deleteListComment,
    getCommentsForUserByProject,

    // credentials
    updateCredential,

    // jobs
    getAllJobs,
    getJob,
    addJob,
    updateJob,
    addJobApplication,
    getJobApplication,
    deleteJobApplication,
    getJobsByAccount,
    deleteJob,
    getApplicationById,
    getTeamJobs,
    getFeaturedJobs,
    getJobStatsByTeam,
    getJobSource,

    // concepts
    getProjects,
    addProject,
    updateProject,
    getProject,
    getProjectByPublicId,
    addProjectClient,
    addConcept,
    updateConcept,
    addConceptMedia,
    removeConceptMedia,
    searchConcepts,
    deleteConcept,
    addConceptTag,
    removeConceptTag,
    deleteProject,
    searchProjects,
    addConceptLink,
    removeConceptLink,
    duplicateConcept,
    generateImage,
    addGeneratedImage,
    addLinksToConcepts,
    getProjectPostStats,
    getUsersFeaturedProjects,
    getFeaturedProjectsForHome,
    addCollaboratorToProject,
    updateProjectCollaborator,
    deleteProjectCollaborator,
    getProjectTagSuggestions,
    getProjectCollaborators,
    getSuggestedProjectPosts,
    addPostsToProject,
    removePostFromProject,
    getTeamProjectRoles,
    createTeamProjectRole,
    updateTeamProjectRole,
    deleteTeamProjectRole,
    getBudgetEstimates,
    addBudgetEstimate,
    updateBudgetEstimate,
    deleteBudgetEstimate,
    getProjectCandidates,
    addCandidate,
    updateCandidate,
    changeCandidateStage,
    rejectCandidate,
    getContracts,
    getContract,
    getContractForCandidate,
    createContract,
    updateContract,
    addProjectCollaborator,
    getProjectCollaboratorDeliverables,
    updateProjectCollaboratorDeliverables,
    addProjectCollaboratorDeliverable,
    updateProjectCollaboratorDeliverable,
    deleteProjectCollaboratorDeliverable,
    enqueueProjectPostImport,
    getProjectAnalytics,

    // project cost management
    getProjectCostSummary,
    getCollaboratorCosts,
    setPostCost,
    getProjectPostCosts,

    // auto credits
    upsertAutoCredit,
    deleteAutoCredit,
    countAutoCreditsToCreate,

    // event signups/Sports Summit
    getEventSignup,
    addEventSignup,
    updateEventSignup,
    getGSS25Signups,

    getWorkedTogether,
    getWorkedTogetherUses,

    getQuestions,
    addQuestion,
    updateQuestion,
    deleteQuestion,

    // content lists
    addPostToList,
    removeItemFromList,
    getProjectLists,
    addItemToList,
    addMediaToList,
    addLinkToList,
    getContentListItems,
    updateContentListItem,
    bulkAddItemsToList,

    // linkedin
    getCurrentUsersPages,

    // caches
    clearAPICache,
    getAPICaches,

    // admin
    sendTestEmail,
    analyzePost,
    getHeardAboutAdminOptions,
    addHeardAboutOption,
    deleteHeardAboutOption,
    executeNaturalLanguageQuery,
    explainSQLQuery,

    // admin checkout
    createAdminCheckout,

    // additional metrics
    getProjectPostAdditionalMetrics,
    updateProjectPostAdditionalMetrics,
    getProjectAdditionalMetricsKeys,
    importProjectPostCSV,
    checkMetricForUse,
    deleteMetricForProject,

    // tubular
    getAllTubularData,
    upsertTubularData,
  };
};
